import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection, AnimatedHeading } from "@/components/ui/AnimatedSection";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ExternalLink, Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import gemueseregalImg from "@/assets/gemueseregal-3.webp";
import { SEO } from "@/components/SEO";

interface NewsItem {
  src: string;
  alt: string;
  width: number;
  height: number;
  title: string;
  objectFit?: "contain" | "cover";
}

const newsItems: NewsItem[] = [
  {
    src: "/lovable-uploads/lieblingsmarkt-2027.webp",
    alt: "Urkunde Lieblingsmarkt Main-Taunus-Kreis 2027, Kategorie Supermärkte",
    width: 1079,
    height: 1525,
    title: "Lieblingsmarkt Main-Taunus-Kreis 2027",
    objectFit: "contain",
  },
  {
    src: "/lovable-uploads/frische-award-2026.webp",
    alt: "Glückwunsch zum REWE Frische Award 2026 für das O&G Team",
    width: 1079,
    height: 1048,
    title: "REWE Frische Award Sieger 2026",
    objectFit: "contain",
  },
  {
    src: "/lovable-uploads/single-shopping.webp",
    alt: "Single Shopping bei REWE V. Gahndi – jeden Freitag mit dem pinken Einkaufskorb neue Kontakte knüpfen",
    width: 684,
    height: 1536,
    title: "Single Shopping – jeden Freitag",
    objectFit: "contain",
  },
  {
    src: "/lovable-uploads/aus-deiner-region.webp",
    alt: "Aus deiner Region – frische, saisonale und regionale Produkte bei REWE V. Gahndi",
    width: 1600,
    height: 963,
    title: "Aus deiner Region – frisch, nah, echt",
    objectFit: "contain",
  },
];

export default function Aktuelles() {
  const [activeImage, setActiveImage] = useState<NewsItem | null>(null);
  const [open, setOpen] = useState(false);

  const openImage = (item: NewsItem) => {
    setActiveImage(item);
    setOpen(true);
  };

  return (
    <Layout>
      <SEO title="Aktuelles | REWE V. Gahndi" description="Aktuelle News, Aktionen und Highlights von REWE V. Gahndi in Flörsheim-Weilbach." path="/aktuelles" />
      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${gemueseregalImg})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative container-custom py-20">
          <AnimatedHeading as="h1" subtitle="Aktuelles">
            Neuigkeiten aus dem <span className="text-gold-gradient">Markt</span>
          </AnimatedHeading>
          <AnimatedSection>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto text-lg">
              Bleib auf dem Laufenden über Aktionen, neue Produkte und alles rund um deinen REWE Markt.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Neuigkeiten Section */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedHeading subtitle="Neuigkeiten">
            Das gibt es <span className="text-gold-gradient">Neues</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {newsItems.map((item, index) => (
              <AnimatedSection key={item.src} delay={index * 0.1}>
                <article className="card-premium overflow-hidden h-full flex flex-col">
                  <button
                    type="button"
                    onClick={() => openImage(item)}
                    className="relative w-full h-80 bg-black/20 group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
                    aria-label={`${item.title} vergrößern`}
                  >
                    <img
                      src={item.src}
                      alt={item.alt}
                      width={item.width}
                      height={item.height}
                      loading="lazy"
                      className={`w-full h-full ${item.objectFit === "cover" ? "object-cover" : "object-contain"} transition-transform duration-300 group-hover:scale-[1.02]`}
                    />
                    <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      Vergrößern
                    </span>
                  </button>
                  <div className="p-6 flex-1">
                    <h3 className="text-xl font-heading font-semibold mb-2 text-primary">{item.title}</h3>
                    {item.title === "Lieblingsmarkt Main-Taunus-Kreis 2027" && (
                      <p className="text-muted-foreground">
                        Wir wurden von der Lebensmittel Praxis auf Basis einer bundesweiten Verbraucherbefragung als
                        Lieblingsmarkt der Region in der Kategorie Supermärkte ausgezeichnet. Danke für euer Vertrauen!
                      </p>
                    )}
                    {item.title === "REWE Frische Award Sieger 2026" && (
                      <p className="text-muted-foreground">
                        Unser Obst-&amp;-Gemüse-Team hat sich mit einem kreativen Sonderaufbau gegen über 450 Einsendungen
                        durchgesetzt und gehört zu den Gewinnern des REWE Frische Awards der Region Mitte.
                      </p>
                    )}
                    {item.title === "Single Shopping – jeden Freitag" && (
                      <p className="text-muted-foreground">
                        Mit dem pinken Einkaufskorb zeigst du freitags: Ich bin offen für ein nettes Gespräch.
                        Locker, respektvoll und ganz ohne Druck – vielleicht entsteht beim Einkauf ein schöner Kontakt.
                      </p>
                    )}
                    {item.title === "Aus deiner Region – frisch, nah, echt" && (
                      <p className="text-muted-foreground">
                        Entdecke die Vielfalt direkt vor deiner Haustür: beste Qualität aus der Region,
                        persönlich und nah – stark für unsere Region mit lokalen Betrieben und kurzen Wegen.
                      </p>
                    )}
                  </div>
                </article>
              </AnimatedSection>
            ))}
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-1 border-none bg-transparent shadow-none">
              <DialogTitle className="sr-only">{activeImage?.title ?? "Bild vergrößert"}</DialogTitle>
              <DialogDescription className="sr-only">Klicke außerhalb oder auf das X um zu schließen.</DialogDescription>
              {activeImage && (
                <img
                  src={activeImage.src}
                  alt={activeImage.alt}
                  width={activeImage.width}
                  height={activeImage.height}
                  className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg"
                />
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedHeading subtitle="Folge uns">
            Bleib auf dem <span className="text-gold-gradient">Laufenden</span>
          </AnimatedHeading>

          <AnimatedSection>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
              Folge uns auf Social Media für die neuesten Angebote, Aktionen und 
              Einblicke hinter die Kulissen unseres Marktes.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {/* Facebook Card */}
            <AnimatedSection>
              <a 
                href="https://www.facebook.com/people/Rewe-V-Gahndi/100063530618457/"
                target="_blank" 
                rel="noopener noreferrer"
                className="card-premium text-center hover:border-primary/50 transition-all group block"
              >
                <Facebook className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">Facebook</h3>
                <p className="text-muted-foreground text-sm">@Rewe-V-Gahndi</p>
              </a>
            </AnimatedSection>

            {/* Instagram Card */}
            <AnimatedSection delay={0.1}>
              <a 
                href="https://www.instagram.com/rewe_v._gahndi/"
                target="_blank" 
                rel="noopener noreferrer"
                className="card-premium text-center hover:border-primary/50 transition-all group block"
              >
                <Instagram className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">Instagram</h3>
                <p className="text-muted-foreground text-sm">@rewe_v._gahndi</p>
              </a>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Links Section */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedHeading subtitle="Mehr entdecken">
            Weitere <span className="text-gold-gradient">Infos</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <AnimatedSection>
              <a
                href="https://www.rewe.de/marktseite/floersheim-weilbach/240367/rewe-markt-industriestrasse-40/?market-flyer=active#"
                target="_blank"
                rel="noopener noreferrer"
                className="card-premium flex items-center justify-between group hover:border-primary/50 transition-all"
              >
                <div className="text-center flex-1">
                  <h3 className="font-heading font-semibold group-hover:text-primary transition-colors">
                    Aktuelle Angebote
                  </h3>
                  <p className="text-sm text-muted-foreground">Online-Prospekt ansehen</p>
                </div>
                <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              </a>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <a
                href="https://www.rewe.de/rezepte/"
                target="_blank"
                rel="noopener noreferrer"
                className="card-premium flex items-center justify-between group hover:border-primary/50 transition-all"
              >
                <div className="text-center flex-1">
                  <h3 className="font-heading font-semibold group-hover:text-primary transition-colors">
                    Rezeptideen
                  </h3>
                  <p className="text-sm text-muted-foreground">Inspiration zum Kochen</p>
                </div>
                <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              </a>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <Link
                to="/kontakt"
                className="card-premium flex items-center justify-between group hover:border-primary/50 transition-all"
              >
                <div className="text-center flex-1">
                  <h3 className="font-heading font-semibold group-hover:text-primary transition-colors">
                    Kontakt
                  </h3>
                  <p className="text-sm text-muted-foreground">Schreib uns eine Nachricht</p>
                </div>
                <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
}
